import { useRef } from 'react';

/**
 * Grabbing an angle and turning it, for the simulator dials.
 *
 * Extracted from `sensor-rotation-view.tsx` when Tilt needed a dial of its own. What is shared is
 * the POINTER work, and nothing else: a dial's drawing is its own business, and Rotation's full
 * circle and Tilt's half-circle protractor are genuinely different pictures. Keeping only this in common
 * is what stops one of them growing a `mode` prop that bends it into the other.
 *
 * Three pieces, each of which was a bug before it was a line of code:
 *
 *  - POINTER CAPTURE plus `preventDefault`. Capture alone is not enough: once the pointer passes
 *    the browser's own drag threshold it starts dragging the SVG as an image, which fires
 *    `pointercancel` and drops the grab. The symptom is a drag that tracks for a moment and then
 *    lifts the whole drawing away under the cursor. `touch-none` and `select-none` do not cover
 *    it either.
 *  - A GRAB OFFSET. Without it the handle snapped to the pointer on press, so grabbing the middle
 *    of a phone sitting at 40 degrees jumped it to wherever the finger was. Holding the offset is
 *    what makes it feel like turning a thing rather than teleporting it.
 *  - NEAREST-EQUIVALENT unwrapping, so a drag across the seam carries on in the direction the hand
 *    is going instead of spinning the long way round. Harmless on a dial with no seam, and the
 *    caller's `onChange` is free to clamp.
 */
export interface DialGeometry {
  /** The SVG's own coordinate extent — the dial is assumed square and centred. */
  size: number;
}

export function useDialDrag<H extends string>({
  svgRef, size, disabled, handles, onChange, bearing,
}: DialGeometry & {
  svgRef: React.RefObject<SVGSVGElement | null>;
  disabled?: boolean;
  /**
   * The INVERSE of the view's own `pointAt`, as degrees from the dial's middle.
   *
   * Defaults to Rotation's convention — zero pointing left, rising clockwise — because that is
   * where this code came from. Tilt counts the other way, and letting it take the default is
   * exactly the bug that shipped: the hit-testing inverted an x the drawing did not, so the dial
   * turned opposite to the hand. A dial's drawing and its hit-testing are two halves of one fact,
   * so the half that differs is a parameter rather than an assumption.
   */
  bearing?: (dx: number, dy: number) => number;
  /**
   * Where each draggable handle currently sits, in degrees. The press picks the nearest by ANGLE
   * rather than by pixels: the handles share one circle, so the only thing separating them is
   * where round it the press landed — and an angular comparison handles the seam, where 350 and
   * 10 are twenty degrees apart while a pixel comparison calls them opposite.
   */
  handles: Record<H, number>;
  onChange: (handle: H, degrees: number) => void;
}) {
  const grabbing = useRef<H | null>(null);
  const grabOffset = useRef(0);
  const centre = size / 2;

  /**
   * The pointer's bearing from the dial's middle, in the view's own convention — see `bearing`.
   *
   * Null too near the middle, where every direction is a pixel away and the bearing is noise.
   */
  function bearingAt(e: React.PointerEvent, near: number): number | null {
    const box = svgRef.current?.getBoundingClientRect();
    if (!box) return null;
    const dx = ((e.clientX - box.left) / box.width) * size - centre;
    const dy = ((e.clientY - box.top) / box.height) * size - centre;
    if (Math.hypot(dx, dy) < 12) return null;
    const raw = bearing ? bearing(dx, dy) : (Math.atan2(-dy, -dx) * 180) / Math.PI;
    return raw + Math.round((near - raw) / 360) * 360;
  }

  function onPointerDown(e: React.PointerEvent) {
    if (disabled) return;
    const entries = Object.entries(handles) as [H, number][];
    if (entries.length === 0) return;
    const at = bearingAt(e, entries[0]![1]);
    if (at == null) return;
    const gap = (a: number) => Math.abs(((at - a + 540) % 360) - 180);
    const [nearest] = entries.reduce((best, cur) => (gap(cur[1]) < gap(best[1]) ? cur : best));
    grabbing.current = nearest;
    grabOffset.current = handles[nearest] - at;
    e.currentTarget.setPointerCapture(e.pointerId);
    e.preventDefault();
  }

  function onPointerMove(e: React.PointerEvent) {
    const held = grabbing.current;
    if (!held) return;
    const at = bearingAt(e, handles[held]);
    if (at == null) return;
    onChange(held, at + grabOffset.current);
  }

  const release = () => { grabbing.current = null; };

  /** Spread onto the `<svg>`. `onDragStart` is belt and braces with the `preventDefault` above:
   *  a drag can still begin outside the handlers' reach. (`draggable` is not in React's SVG prop
   *  types, so the refusal goes through the event.) */
  return {
    onDragStart: (e: React.DragEvent) => e.preventDefault(),
    onPointerDown,
    onPointerMove,
    onPointerUp: release,
    onPointerCancel: release,
  };
}
